import { useRef, useContext } from 'react'
import { motion } from 'framer-motion'
import PageLayout from '../components/PageLayout'
import { useInView } from '../hooks/useInView'
import { RouteTransitionContext } from '../components/AnimatedRoutes'

type Health = 'operational' | 'degraded' | 'outage' | 'maintenance'

const systems: { name: string; description: string; health: Health; uptime: string }[] = [
  {
    name: 'Marketplace web app',
    description: 'oakplatforms.com and moderator dashboards.',
    health: 'operational',
    uptime: '99.99%',
  },
  {
    name: 'Marketplace mobile apps',
    description: 'iOS and Android apps published to Oak-powered marketplaces.',
    health: 'operational',
    uptime: '99.98%',
  },
  {
    name: 'Public APIs',
    description: 'REST and GraphQL endpoints used by customer integrations.',
    health: 'operational',
    uptime: '99.99%',
  },
  {
    name: 'Payments',
    description: 'Checkout, refunds, and card processing.',
    health: 'operational',
    uptime: '99.99%',
  },
  {
    name: 'Payouts',
    description: 'Seller payouts and balance movement.',
    health: 'operational',
    uptime: '99.97%',
  },
  {
    name: 'Messaging',
    description: 'In-app direct messages, threads, and notifications.',
    health: 'operational',
    uptime: '99.98%',
  },
  {
    name: 'Cortex analytics',
    description: 'Real-time marketplace metrics and the Cortex API.',
    health: 'operational',
    uptime: '99.96%',
  },
  {
    name: 'Agents discovery',
    description: 'Agent-facing catalog, search, and ordering endpoints.',
    health: 'operational',
    uptime: '99.95%',
  },
]

const healthLabel: Record<Health, string> = {
  operational: 'All systems operational',
  degraded: 'Degraded performance',
  outage: 'Service outage',
  maintenance: 'Scheduled maintenance',
}

const healthColor: Record<Health, { dot: string; text: string }> = {
  operational: { dot: 'bg-oak-green', text: 'text-oak-black' },
  degraded: { dot: 'bg-amber-500', text: 'text-oak-black' },
  outage: { dot: 'bg-red-500', text: 'text-oak-black' },
  maintenance: { dot: 'bg-blue-500', text: 'text-oak-black' },
}

const overall: Health = systems.every((s) => s.health === 'operational')
  ? 'operational'
  : systems.some((s) => s.health === 'outage')
    ? 'outage'
    : 'degraded'

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

function StatusHero() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.3 })
  const isTransitioning = useContext(RouteTransitionContext)
  const color = healthColor[overall]

  return (
    <section ref={ref} className="bg-oak-cream px-6 pt-32 pb-16 md:pt-44 md:pb-20">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={!isTransitioning && isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
        transition={{ duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const }}
        className="mx-auto max-w-nav px-6 text-center"
      >
        <div className="inline-flex items-center gap-3 rounded-pill bg-white px-5 py-2.5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <span className={`relative flex h-3 w-3`}>
            <span
              className={`absolute inline-flex h-full w-full animate-ping rounded-full ${color.dot} opacity-75`}
            />
            <span className={`relative inline-flex h-3 w-3 rounded-full ${color.dot}`} />
          </span>
          <span className={`text-[14px] font-medium tracking-[-0.2px] ${color.text}`}>
            {healthLabel[overall]}
          </span>
        </div>
        <h1 className="mx-auto mt-6 max-w-[820px] font-serif text-[clamp(40px,7vw,96px)] leading-[0.95] tracking-[-2px] text-oak-black">
          Oak platform status
        </h1>
        <p className="mx-auto mt-6 max-w-[520px] text-[clamp(16px,1.8vw,22px)] font-medium leading-[1.4] tracking-[-0.3px] text-oak-black/60">
          Real-time health for every system our customers depend on. Subscribe to get notified the moment something changes.
        </p>
      </motion.div>
    </section>
  )
}

function SystemsSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.1 })
  const isTransitioning = useContext(RouteTransitionContext)

  return (
    <section ref={ref} className="rounded-section bg-white px-6 py-16 md:px-16 md:py-20">
      <div className="mx-auto max-w-nav px-6">
        <h2 className="text-[clamp(20px,2vw,24px)] font-semibold tracking-[-0.4px] text-oak-black">
          Systems
        </h2>

        <motion.div
          initial="hidden"
          animate={!isTransitioning && isInView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
          className="mt-8 overflow-hidden rounded-3xl border border-oak-black/10"
        >
          {systems.map((s, i) => {
            const color = healthColor[s.health]
            return (
              <motion.div
                key={s.name}
                variants={fadeUp}
                className={`flex flex-col gap-3 px-6 py-5 md:flex-row md:items-center md:justify-between md:px-8 md:py-6 ${
                  i !== 0 ? 'border-t border-oak-black/10' : ''
                }`}
              >
                <div className="max-w-[520px]">
                  <div className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${color.dot}`} />
                    <h3 className="text-[clamp(16px,1.5vw,18px)] font-semibold tracking-[-0.3px] text-oak-black">
                      {s.name}
                    </h3>
                  </div>
                  <p className="mt-1.5 pl-5 text-[14px] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
                    {s.description}
                  </p>
                </div>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}

export default function StatusPage() {
  return (
    <PageLayout>
      <StatusHero />
      <SystemsSection />
    </PageLayout>
  )
}
